/**
 * Production-grade Timeline Engine with sub-millisecond precision, keyframing,
 * multi-track parallel/sequential scheduling, dynamic speed, and scrubbing.
 */
import { EventEmitter } from './EventEmitter';
import { Easing, getEasing, EasingFunction } from './Easing';

export interface Keyframe<T = any> {
  time: number; // in seconds
  value: T;
  easing?: string | EasingFunction;
  interpolator?: (start: T, end: T, t: number) => T;
}

export interface AnimationTrack {
  id: string;
  targetId: string;
  property: string;
  keyframes: Keyframe[];
  enabled: boolean;
  onUpdate?: (value: any, targetId: string, property: string) => void;
}

export interface TimelineAction {
  id: string;
  startTime: number; // in seconds
  duration: number; // in seconds
  name: string;
  targetId?: string;
  execute: (localProgress: number, absoluteTime: number, delta: number) => void;
  onStart?: () => void;
  onComplete?: () => void;
  isStarted?: boolean;
  isCompleted?: boolean;
}

export type PlaybackState = 'idle' | 'playing' | 'paused' | 'seeking' | 'ended';

export class Timeline extends EventEmitter {
  private currentTime: number = 0;
  private duration: number = 10; // default 10s
  private playbackState: PlaybackState = 'idle';
  private speed: number = 1.0;
  private loop: boolean = false;
  private rafId: number | null = null;
  private lastTimestamp: number = 0;

  private tracks: Map<string, AnimationTrack> = new Map();
  private actions: TimelineAction[] = [];
  private markers: Map<string, number> = new Map();

  constructor(duration: number = 10) {
    super();
    this.duration = Math.max(0.1, duration);
  }

  public getDuration(): number {
    return this.duration;
  }

  public setDuration(duration: number): void {
    this.duration = Math.max(0.1, duration);
    this.emit('durationchange', this.duration);
  }

  public getCurrentTime(): number {
    return this.currentTime;
  }

  public getPlaybackState(): PlaybackState {
    return this.playbackState;
  }

  public getSpeed(): number {
    return this.speed;
  }

  public setSpeed(speed: number): void {
    this.speed = Math.max(0.05, Math.min(10, speed));
    this.emit('speedchange', this.speed);
  }

  public isLooping(): boolean {
    return this.loop;
  }

  public setLoop(loop: boolean): void {
    this.loop = loop;
    this.emit('loopchange', this.loop);
  }

  public addMarker(name: string, time: number): void {
    this.markers.set(name, time);
    this.emit('markeradded', { name, time });
  }

  public getMarkerTime(name: string): number | undefined {
    return this.markers.get(name);
  }

  public getMarkers(): Map<string, number> {
    return new Map(this.markers);
  }

  public addTrack(track: AnimationTrack): void {
    // Sort keyframes by time
    track.keyframes.sort((a, b) => a.time - b.time);
    this.tracks.set(track.id, track);

    // Expand duration if track exceeds it
    const lastKey = track.keyframes[track.keyframes.length - 1];
    if (lastKey && lastKey.time > this.duration) {
      this.setDuration(lastKey.time);
    }
  }

  public removeTrack(trackId: string): void {
    this.tracks.delete(trackId);
  }

  public getTracks(): AnimationTrack[] {
    return Array.from(this.tracks.values());
  }

  public addAction(action: TimelineAction): void {
    this.actions.push(action);
    const endTime = action.startTime + action.duration;
    if (endTime > this.duration) {
      this.setDuration(endTime);
    }
  }

  public removeAction(actionId: string): void {
    this.actions = this.actions.filter(a => a.id !== actionId);
  }

  public getActions(): TimelineAction[] {
    return [...this.actions];
  }

  public clear(): void {
    this.pause();
    this.tracks.clear();
    this.actions = [];
    this.markers.clear();
    this.currentTime = 0;
    this.playbackState = 'idle';
  }

  public play(): void {
    if (this.playbackState === 'playing') return;

    if (this.currentTime >= this.duration) {
      this.currentTime = 0;
      this.resetActionStates();
    }

    this.playbackState = 'playing';
    this.lastTimestamp = performance.now();
    this.emit('play', this.currentTime);

    this.tick(performance.now());
  }

  public pause(): void {
    if (this.playbackState !== 'playing') return;
    this.playbackState = 'paused';
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.emit('pause', this.currentTime);
  }

  public seek(time: number, emitEvent = true): void {
    const prevTime = this.currentTime;
    this.currentTime = Math.max(0, Math.min(this.duration, time));

    // Reset action states so evaluate can properly transition them
    for (const action of this.actions) {
      action.isStarted = false;
      action.isCompleted = false;
    }

    // Force frame evaluation
    this.evaluate(this.currentTime, 0);

    if (emitEvent) {
      this.emit('seek', { time: this.currentTime, prevTime });
      this.emit('timeupdate', this.currentTime);
    }

    if (this.currentTime >= this.duration && !this.loop && this.playbackState === 'playing') {
      this.pause();
      this.playbackState = 'ended';
      this.emit('ended', this.currentTime);
    }
  }

  public restart(): void {
    this.seek(0, false);
    this.resetActionStates();
    this.play();
  }

  private resetActionStates(): void {
    for (const act of this.actions) {
      act.isStarted = false;
      act.isCompleted = false;
    }
  }

  private tick = (timestamp: number): void => {
    if (this.playbackState !== 'playing') return;

    const deltaMs = timestamp - this.lastTimestamp;
    this.lastTimestamp = timestamp;

    const deltaSec = (deltaMs / 1000) * this.speed;
    const newTime = this.currentTime + deltaSec;

    if (newTime >= this.duration) {
      if (this.loop) {
        this.currentTime = newTime % this.duration;
        this.resetActionStates();
        this.evaluate(this.currentTime, deltaSec);
        this.emit('timeupdate', this.currentTime);
        this.emit('loop', this.currentTime);
        this.rafId = requestAnimationFrame(this.tick);
      } else {
        this.currentTime = this.duration;
        this.evaluate(this.currentTime, deltaSec);
        this.emit('timeupdate', this.currentTime);
        this.playbackState = 'ended';
        this.emit('ended', this.currentTime);
      }
    } else {
      this.currentTime = newTime;
      this.evaluate(this.currentTime, deltaSec);
      this.emit('timeupdate', this.currentTime);
      this.rafId = requestAnimationFrame(this.tick);
    }
  };

  /**
   * Evaluate all tracks and actions at specified time point.
   */
  public evaluate(time: number, delta: number): void {
    // 1. Evaluate keyframe tracks
    for (const track of this.tracks.values()) {
      if (!track.enabled || track.keyframes.length === 0) continue;

      const val = this.interpolateTrack(track, time);
      if (track.onUpdate) {
        track.onUpdate(val, track.targetId, track.property);
      }
      this.emit('trackupdate', { trackId: track.id, targetId: track.targetId, property: track.property, value: val });
    }

    // 2. Evaluate active and lifecycle actions
    for (const action of this.actions) {
      const startTime = action.startTime;
      const endTime = startTime + action.duration;

      if (time >= startTime && time <= endTime) {
        if (!action.isStarted) {
          action.isStarted = true;
          action.onStart?.();
          this.emit('actionstart', action);
        }

        const localProgress = action.duration > 0 ? (time - startTime) / action.duration : 1;
        action.execute(Math.max(0, Math.min(1, localProgress)), time, delta);
      } else if (time > endTime) {
        if (!action.isStarted) {
          action.isStarted = true;
          action.onStart?.();
          this.emit('actionstart', action);
        }
        if (!action.isCompleted) {
          action.isCompleted = true;
          action.execute(1, time, delta);
          action.onComplete?.();
          this.emit('actioncomplete', action);
        }
      } else if (time < startTime) {
        if (action.isStarted || action.isCompleted) {
          action.execute(0, time, delta);
          action.isStarted = false;
          action.isCompleted = false;
        }
      }
    }
  }

  private interpolateTrack(track: AnimationTrack, time: number): any {
    const kfs = track.keyframes;
    if (time <= kfs[0].time) return kfs[0].value;
    if (time >= kfs[kfs.length - 1].time) return kfs[kfs.length - 1].value;

    // Find bounding keyframes
    let k1 = kfs[0];
    let k2 = kfs[1];

    for (let i = 0; i < kfs.length - 1; i++) {
      if (time >= kfs[i].time && time <= kfs[i + 1].time) {
        k1 = kfs[i];
        k2 = kfs[i + 1];
        break;
      }
    }

    const range = k2.time - k1.time;
    if (range <= 0) return k1.value;

    const rawT = (time - k1.time) / range;
    const easeFn = typeof k2.easing === 'function' ? k2.easing : getEasing(k2.easing || 'linear');
    const easedT = easeFn(rawT);

    if (k2.interpolator) {
      return k2.interpolator(k1.value, k2.value, easedT);
    }

    // Default numeric interpolation
    if (typeof k1.value === 'number' && typeof k2.value === 'number') {
      return k1.value + (k2.value - k1.value) * easedT;
    }

    // Step value
    return rawT < 0.5 ? k1.value : k2.value;
  }
}
